import React, {useEffect, useRef, useState} from "react";
import {
    DAYS_MS,
    getServerDate,
    getTimerCounterDataFromHtml,
    HOURS_MS, MIN_MS
} from "../clock-provider";
import GeneralTimer from './types/general-timer';
import GeneralDays from './types/general-days';
import ShortDays from './types/short-days';
import ShortDaysLeft from './types/short-days-left';
import ShortDaysSquare from './types/short-days-square';

/**
 * Count Down Timer
 * https://wavesaudio.atlassian.net/browse/DSWEB-3116
 * Tal: 27/4/2023
 * Modified by Miriam: 31/3/2025
 */
export interface ITimerCountDownProps {

    // Apr 01 2025 05:00:00 GMT-0400 (Eastern Daylight Time) ---> in milliseconds (1743498000000)
    expiredWhenNumber: number;

    timerType: string;
    dataClass?: string;
    dataClassEx?: string;
    loadDataFromHtml? :string;
}

const baseBrowserDateTimeMs = (new Date()).getTime();

const checkIsExpired = (expiredWhenNumber: number, baseServerDateTimeMs: number) => {
    // take the number of time till the end of the sale in server time that was calculated on page load.
    // every second decrease this value
    const currentBrowserTime = (new Date()).getTime();
    const diff = expiredWhenNumber - baseServerDateTimeMs - (currentBrowserTime - baseBrowserDateTimeMs);

    // calculate the number of hours until the end of the sale
    const hoursTillEndOfDiscount = diff / HOURS_MS;

    return hoursTillEndOfDiscount <= 0;
};

const TimerCountDown = (props: ITimerCountDownProps) => {

    let timerType: string = props?.timerType;
    let dataClass: string = props?.dataClass ?? "bg-black px-5 py-2 text-white mr-3";
    let dataClassEx: string = props?.dataClassEx ?? "text-xs";

    // sale end date-time in milliseconds ----------------------
    let expiredWhenNumber = props.expiredWhenNumber;

    if(props?.loadDataFromHtml && props?.loadDataFromHtml !== '') {
        const timerFromHtml = getTimerCounterDataFromHtml(props?.loadDataFromHtml);
        if(timerFromHtml){
            timerType= timerFromHtml.timerType ?? 'general';
            dataClass= timerFromHtml.dataClass ?? "bg-black px-5 py-2 text-white mr-3";
            dataClassEx= timerFromHtml.dataClassEx ?? "text-xs";
            expiredWhenNumber= timerFromHtml.expiredWhenNumber;
        }
    }

    const containerRef = useRef<HTMLSpanElement>(null);

    const [ baseServerDateTimeMs ] = useState(getServerDate()); // server date-time in milliseconds

    const [days, setDays] = useState(0);
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const updateTimer = () => {

            const isExpired = checkIsExpired(expiredWhenNumber, baseServerDateTimeMs);

            if(isExpired) {
                setIsExpired(true);
                setDays(0);
                setHours(0);
                setMinutes(0);
                setSeconds(0);

                clearInterval(calcCounterInterval);

                if (containerRef.current?.parentElement?.parentElement) {
                    containerRef.current.parentElement.parentElement.classList.add('hidden');
                }
                return;
            }

            // Take the number of time till the end of the sale
            // in server time that was calculated on page load.
            // Every second decrease this value.
            const currentBrowserTime = (new Date()).getTime();
            const diff = expiredWhenNumber - baseServerDateTimeMs - (currentBrowserTime - baseBrowserDateTimeMs);

            setDays(Math.floor(diff / DAYS_MS));
            setHours(Math.floor((diff % DAYS_MS) / HOURS_MS));
            setMinutes(Math.floor((diff % HOURS_MS) / MIN_MS));
            setSeconds(Math.floor((diff % HOURS_MS % MIN_MS) / 1000));
        };

        const calcCounterInterval = setInterval(updateTimer, 1000);

        return () => {
            clearInterval(calcCounterInterval);
        };
    }, [
        expiredWhenNumber,
        baseServerDateTimeMs,
    ]);


    return (
        <span ref={ containerRef }>
            {
                timerType === 'general' &&
                <GeneralTimer
                    isExpired={ isExpired }
                    days={ days }
                    hours={ hours }
                    minutes={ minutes }
                    seconds={ seconds }
                />
            }

            {
                timerType === 'general-days' &&
                <GeneralDays
                    isExpired={ isExpired }
                    days={ days }
                    hours={ hours }
                    minutes={ minutes }
                    seconds={ seconds }
                />
            }

            {
                timerType === 'short-days' &&
                <ShortDays
                    isExpired={ isExpired }
                    days={ days }
                    hours={ hours }
                    minutes={ minutes }
                    seconds={ seconds }
                />
            }

            {
                timerType === 'short-days-left' &&
                <ShortDaysLeft
                    isExpired={ isExpired }
                    days={ days }
                    hours={ hours }
                    minutes={ minutes }
                    seconds={ seconds }
                />
            }

            {
                timerType === 'style-days-square' &&
                <ShortDaysSquare
                    isExpired={ isExpired }
                    days={ days }
                    hours={ hours }
                    minutes={ minutes }
                    seconds={ seconds }
                    dataClass={ dataClass }
                    dataClassEx={ dataClassEx }
                />
            }
        </span>
    );
};

export default TimerCountDown;

